import React, { useState, useEffect, useMemo } from 'react';
import { StyleSheet, View, ScrollView, RefreshControl, ActivityIndicator } from 'react-native';
import { Text } from '@/components/common/text';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { RootStackParamList } from '@/navigation/RootNavigator';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { PaymentSummaryProvider } from '@/features/payments/context/PaymentSummaryContext';
import { TransactionDetailsModal } from '@/features/payments/components/mobile/TransactionDetailsModal';
import { MobileAutopayDetailsModal } from '@/screens/residents/payments/mobile/MobileAutopayDetailsModal';
import type { AutopayChange } from '@/screens/residents/payments/mobile/MobileAutopayDetailsModal';
import { useAppSelector, useAppDispatch } from '@/store';
import { selectLeaseIds, selectConnectionLeaseIds, linkedAcctsConnections, linkedAcctsConnectionsLoading } from '@/store/selectors';
import { useGetLeaseQuery } from '@/store/api';
import { usePaymentBalance } from '@/features/payments/api/paymentHooks';
import { usePaymentMethodsList } from '@/features/payments/hooks/usePaymentMethodsList';
import { useLeaseHistory } from '@/features/payments/hooks/useLeaseHistory';
import type { HistoryRow } from '@/features/payments/hooks/useLeaseHistory';
import { fetchLinkedAccounts } from '@/store/slices/residents/homeSlice';
import { PaymentsScreenSkeleton } from './PaymentsScreenSkeleton';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useThemeColors, useThemedStyles } from '@/hooks/residents/useThemeColors';
import { PageHeader, GlassCard } from '@/components/residents/redesign';
import { ScreenBackLink } from '@/components/residents/mobile/ScreenBackLink';
import {
  PaymentBalanceDueCard,
  PaymentMethodsSection,
  RecurringChargesCard,
  TransactionHistorySection,
  AutopaySection,
} from '@/components/residents/redesign/payments';
import type { RecurringChargeItem, AutopayLease } from '@/components/residents/redesign/payments';
import { selectLease, hasAutoPay, buildAutopayLease } from '@/features/payments/utils/leaseAutoPay';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ChargeBreakdown } from '@/features/payments/components/shared/ChargeBreakdown';


const GroupIcon = SVG_COMPONENTS.GROUP;
const HP = REDESIGN_SPACING.horizontalPadding;

interface LeaseDisplayData {
  leaseId: number;
  propertyName: string;
  unitNumber: string;
  balanceDue: number;
  accountCredit: number;
  balanceItems: any[] | undefined;
  autopayEnabled: boolean;
  autopayLease: AutopayLease;
  recurringCharges: RecurringChargeItem[];
  isConnection: boolean;
  paymentsBlocked: boolean;
  pendingPaymentsCount: number;
}

interface LeaseDataResult {
  data: LeaseDisplayData | null;
  isLoading: boolean;
  /** Resolves once the lease has been fetched again (never rejects on a failed fetch). */
  refetch: () => Promise<unknown>;
}

const useLeaseData = (leaseId: number, isConnection: boolean): LeaseDataResult => {
  const { balanceDue, balanceItems, accountCredit } = usePaymentBalance(leaseId);

  const leaseQuery = useGetLeaseQuery(
    { leaseId, includeTransactions: false, includePayments: true },
    { skip: !leaseId },
  );

  const { isFetching, isError, isSuccess, data: rawData, currentData, refetch } = leaseQuery;

  // A refetch of the same lease keeps `currentData`; only the first load or a
  // lease switch clears it. The view must survive a refetch because the
  // autopay modal lives inside it and would unmount mid-animation.
  const isLoading = isFetching && !currentData;

  const data = useMemo<LeaseDisplayData | null>(() => {
    if (isLoading || isError || !isSuccess || !rawData) return null;

    const response = (rawData as any)?.response ?? rawData;
    const lease = selectLease(rawData);
    const property = lease?.property || {};
    const scheduledCharges = lease?.scheduledCharges || [];
    const users = lease?.users || [];
    const primaryUser = users[0] || {};

    const payments: any[] = response?.payments || [];
    const pendingPaymentsCount = payments.filter(
      (p: any) => !p.settlementStatus || p.settlementStatus === 'Pending Finalization'
    ).length;

    const autopayEnabled = hasAutoPay(lease);
    const propertyName = property?.marketingName || '';
    const autopayLease = buildAutopayLease({ leaseId, lease, primaryUser });

    return {
      leaseId,
      propertyName,
      unitNumber: lease?.unitNumberSpace || lease?.unitNumber || '',
      balanceDue,
      accountCredit,
      balanceItems,
      autopayEnabled,
      autopayLease,
      recurringCharges: scheduledCharges.map((c: any) => ({
        name: c.chargeCode || c.description || 'Charge',
        amount: c.amount ? Number(c.amount) : null,
        frequency: 'Monthly',
      })),
      isConnection,
      paymentsBlocked: !!lease?.paymentsBlocked,
      pendingPaymentsCount,
    };
  }, [leaseId, isLoading, isError, isSuccess, rawData, balanceDue, accountCredit, balanceItems, isConnection]);

  return { data, isLoading, refetch };
};

const PaymentsContent: React.FC = () => {
  const { colors } = useThemeColors();
  const styles = usePaymentsStyles();
  const dispatch = useAppDispatch();
  const insets = useSafeAreaInsets();

  const ownLeaseIds = useAppSelector(selectLeaseIds);
  const connectionLeaseIds = useAppSelector(selectConnectionLeaseIds);
  const linkedAccounts = useAppSelector(linkedAcctsConnections);
  const isLoading = useAppSelector(linkedAcctsConnectionsLoading);
  const [refreshing, setRefreshing] = useState(false);
  const authUser = useAppSelector((s) => (s as any).auth?.user);
  const userId = authUser?.id;

  useEffect(() => {
    if (!linkedAccounts || linkedAccounts.length === 0) {
      dispatch(fetchLinkedAccounts());
    }
  }, [dispatch]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await dispatch(fetchLinkedAccounts()).unwrap();
    } catch (error) {
      console.error('Failed to refresh:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const allLeaseIds = [...ownLeaseIds, ...connectionLeaseIds];
  const hasLeases = allLeaseIds.length > 0;
  const primaryLeaseId = ownLeaseIds[0] || connectionLeaseIds[0];
  const isPrimaryConnection = connectionLeaseIds.includes(primaryLeaseId);

  const {
    rows: transactions,
  } = useLeaseHistory(primaryLeaseId, userId);

  const [selectedTransaction, setSelectedTransaction] = useState<HistoryRow | null>(null);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);

  const handleTransactionPress = (transaction: HistoryRow) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetails(true);
  };

  const handleCloseTransactionDetails = () => {
    setShowTransactionDetails(false);
    setSelectedTransaction(null);
  };

  return (
    <>
      {isLoading && !hasLeases ? (
        <PaymentsScreenSkeleton />
      ) : !hasLeases ? (
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[styles.scrollContent, { paddingBottom: 40 + insets.bottom }]}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.text.primary} />
          }
        >
          <ScreenBackLink style={styles.backLink} />
          <PageHeader title="Payments" />
          <EmptyPaymentsState />
        </ScrollView>
      ) : (
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[styles.scrollContent, { paddingBottom: 40 + insets.bottom }]}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} tintColor={colors.text.primary} />
          }
        >
          {/* The mobile stack hides native headers: like every screen the home
              tiles push, this one draws its own back link (it hides itself when
              the screen is the stack's root). */}
          <ScreenBackLink style={styles.backLink} />
          <LeasePaymentsView
            leaseId={primaryLeaseId}
            isConnection={isPrimaryConnection}
            transactions={transactions}
            onTransactionPress={handleTransactionPress}
          />
        </ScrollView>
      )}
      <TransactionDetailsModal
        visible={showTransactionDetails}
        onClose={handleCloseTransactionDetails}
        transaction={selectedTransaction}
      />
    </>
  );
};

const EmptyPaymentsState: React.FC = React.memo(() => {
  const { colors } = useThemeColors();
  const styles = usePaymentsStyles();
  return (
    <View style={styles.emptyContainer}>
      <GlassCard style={styles.emptyCard}>
        <View style={styles.emptyIconContainer}>
          {GroupIcon && <GroupIcon width={48} height={48} color={colors.text.dimmer} />}
        </View>
        <Text style={styles.emptyTitle}>No recipients</Text>
        <Text style={styles.emptyDescription}>
          Once you receive and accept an invitation your recipients will be displayed here
        </Text>
      </GlassCard>
      <TransactionHistorySection transactions={[]} style={styles.emptyHistorySection} />
    </View>
  );
});

interface LeasePaymentsViewProps {
  leaseId: number;
  isConnection: boolean;
  transactions: HistoryRow[];
  onTransactionPress: (t: HistoryRow) => void;
}

const LeasePaymentsView: React.FC<LeasePaymentsViewProps> = React.memo(({
  leaseId,
  isConnection,
  transactions,
  onTransactionPress,
}) => {
  const { colors } = useThemeColors();
  const styles = usePaymentsStyles();
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const { data: leaseData, isLoading, refetch: refetchLease } = useLeaseData(leaseId, isConnection);
  const { methods: paymentMethods } = usePaymentMethodsList();
  const [showRecurringCharges, setShowRecurringCharges] = useState(false);
  // The autopay modal stays mounted after its first open so UnifiedModal can
  // animate it out; the mode is kept through the exit.
  const [autopayModalMode, setAutopayModalMode] = useState<'setup' | 'view'>('view');
  const [autopayModalVisible, setAutopayModalVisible] = useState(false);
  // Outlives the modal on purpose: from the moment it reports a setup or a
  // removal until this view's refetched lease reflects it, the card reads
  // "Setting up autopay…" / "Removing autopay…" instead of the old state
  // showing through while the modal slides out.
  const [autopayPending, setAutopayPending] = useState<'settingUp' | 'removing' | null>(null);

  if (isLoading || !leaseData) {
    return (
      <>
        <PageHeader title="Payments" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator color={colors.text.muted} />
        </View>
      </>
    );
  }

  const subtitle = leaseData.unitNumber
    ? `${leaseData.propertyName} · Unit ${leaseData.unitNumber}`
    : leaseData.propertyName;

  const handleMakePayment = () => {
    navigation.navigate('MakePayment', { leaseId });
  };

  const handleManagePaymentMethods = () => {
    navigation.navigate('ManageWallet', {});
  };

  const handleAddPaymentMethod = () => {
    navigation.navigate('ManageWallet', { initialScreen: 'add' });
  };

  const handleViewAllTransactions = () => {
    navigation.navigate('TransactionHistory', { leaseId });
  };

  const openAutopayModal = (mode: 'setup' | 'view') => {
    setAutopayModalMode(mode);
    setAutopayModalVisible(true);
  };

  const handleAutopaySetup = () => openAutopayModal('setup');
  const handleAutopayEdit = () => openAutopayModal('view');
  const handleAutopayRemove = () => openAutopayModal('view');

  const handleAutopayUpdated = async (change: AutopayChange) => {
    // An edit keeps the modal open on the new values; nothing stale shows.
    if (change === 'update') return;
    setAutopayPending(change === 'remove' ? 'removing' : 'settingUp');
    try {
      // The modal has already invalidated the lease tag, which started this
      // view's refetch; awaiting refetch() waits on that same request.
      await refetchLease();
    } catch {
      // The card falls back to whatever the lease says now.
    } finally {
      setAutopayPending(null);
    }
  };

  return (
    <>
      <PageHeader title="Payments" subtitle={subtitle} />

      <PaymentBalanceDueCard
        balanceDue={leaseData.balanceDue}
        accountCredit={leaseData.accountCredit}
        autopayEnabled={leaseData.autopayEnabled}
        recurringChargesCount={leaseData.recurringCharges.length}
        onMakePayment={handleMakePayment}
        onViewBreakdown={() => setShowRecurringCharges(!showRecurringCharges)}
        style={styles.sectionSpacing}
      />

      {leaseData.balanceItems && leaseData.balanceItems.length > 0 && (
        <ChargeBreakdown
          balanceItems={leaseData.balanceItems}
          balanceDue={leaseData.balanceDue}
          variant="desktop"
          style={styles.sectionSpacing}
        />
      )}

      {paymentMethods.length > 0 && (
        <PaymentMethodsSection
          methods={paymentMethods}
          onManage={handleManagePaymentMethods}
          onAddNew={handleAddPaymentMethod}
        />
      )}

      <AutopaySection
        leases={[leaseData.autopayLease]}
        onSetup={handleAutopaySetup}
        onEdit={handleAutopayEdit}
        onRemove={handleAutopayRemove}
        removingLeaseId={autopayPending === 'removing' ? leaseId : null}
        settingUpLeaseId={autopayPending === 'settingUp' ? leaseId : null}
      />

      {(showRecurringCharges || leaseData.recurringCharges.length > 0) && (
        <RecurringChargesCard charges={leaseData.recurringCharges} />
      )}

      <TransactionHistorySection
        transactions={transactions}
        maxItems={5}
        onViewAll={handleViewAllTransactions}
        onTransactionPress={onTransactionPress}
        style={styles.sectionSpacing}
      />

      <MobileAutopayDetailsModal
        visible={autopayModalVisible}
        onClose={() => setAutopayModalVisible(false)}
        leaseId={leaseId}
        onAutopayUpdated={handleAutopayUpdated}
        mode={autopayModalMode}
      />
    </>
  );
});

export const ResidentPaymentsScreen: React.FC = () => (
  <PaymentSummaryProvider>
    <PaymentsContent />
  </PaymentSummaryProvider>
);

const usePaymentsStyles = () =>
  useThemedStyles(({ colors }) =>
    StyleSheet.create({
      scrollView: {
        flex: 1,
      },
      scrollContent: {
        paddingHorizontal: HP,
      },
      backLink: {
        marginBottom: -4,
      },
      loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 60,
      },
      sectionSpacing: {
        marginTop: 20,
      },
      emptyContainer: {
        marginTop: 20,
      },
      emptyCard: {
        alignItems: 'center',
        paddingVertical: 40,
      },
      emptyIconContainer: {
        marginBottom: 16,
      },
      emptyTitle: {
        ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
        color: colors.text.primary,
        marginBottom: 8,
        textAlign: 'center',
      },
      emptyDescription: {
        ...REDESIGN_TYPOGRAPHY.body,
        color: colors.text.muted,
        textAlign: 'center',
        lineHeight: 21,
      },
      emptyHistorySection: {
        marginTop: 24,
      },
    }),
  );
